// src/util/enrollment_api_util.js
import axios from "axios";
import { setAuthToken } from "./session_api_util";
import { emitSessionExpired } from "./auth_events";

// ✅ Treat 401 as a "valid" response (so Axios does NOT throw)
// This prevents console red errors + unhandled rejections.
const client = axios.create({
  withCredentials: true,
  validateStatus: (status) => {
    // Resolve all "ok-ish" responses + 401 (expired/invalid session)
    return (status >= 200 && status < 300) || status === 401;
  },
});

// ---------- soft-expire (ONCE) ----------
let didSoftExpire = false;

const softExpireOnce = (reason = "401") => {
  if (didSoftExpire) return;
  didSoftExpire = true;

  const hash = window.location.hash || "";
  if (hash.includes("#/login")) return;

  try {
    sessionStorage.setItem("returnTo", hash || "#/dashboard");
  } catch (e) {}

  try {
    localStorage.removeItem("jwtToken");
  } catch (e) {}

  setAuthToken(false);

  // Mark redux session expired
  try {
    window.__COSMETINK_STORE__?.dispatch?.({
      type: "SET_SESSION_EXPIRED",
      reason,
    });
  } catch (e) {}

  emitSessionExpired(reason);
};

// ---------- request: attach JWT ----------
client.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("jwtToken");
    if (token) {
      const bearer = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
      config.headers = config.headers || {};
      config.headers["Authorization"] = bearer;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ---------- response: handle 401 without throwing ----------
client.interceptors.response.use(
  (response) => {
    if (response?.status === 401) {
      softExpireOnce("401");
      // ✅ return a consistent shape so callers can safely bail
      return { ...response, data: null, __softExpired: true };
    }
    return response;
  },
  (error) => Promise.reject(error)
);

// =======================================
// API HELPERS
// =======================================

export const fetchMyEnrollments = () => client.get("/api/enrollments/me");
export const fetchEnrollmentsForUser = (userId) => client.get(`/api/enrollments/user/${userId}`);

export const updateEnrollmentProgress = (enrollmentId, data) =>
  client.patch(`/api/enrollments/${enrollmentId}/progress`, data);

export const createOrUpdateEnrollment = (payload) => client.post("/api/enrollments", payload);

export const extendEnrollmentAccess = (enrollmentId, months = 3) =>
  client.post(`/api/enrollments/${enrollmentId}/extend-access`, { months });

export const markSectionQuizPassed = (enrollmentId, sectionId) =>
  client.patch(`/api/enrollments/${enrollmentId}/sections/${sectionId}/quiz-pass`);

export const submitModelInfo = (enrollmentId, payload) =>
  client.post(`/api/enrollments/${enrollmentId}/model`, payload);

export const updatePhotoPackageSelection = (enrollmentId, selection) =>
  client.patch(`/api/enrollments/${enrollmentId}/photo-package/selection`, { selection });

export const updatePhotoPackageLink = (enrollmentId, deliveryLink) =>
  client.patch(`/api/enrollments/${enrollmentId}/photo-package/link`, { deliveryLink });

export const updatePhotoPackageAdmin = (enrollmentId, payload = {}) =>
  client.patch(`/api/enrollments/${enrollmentId}/photo-package/link`, payload);

export const fetchPhotoPackageSummary = (studentIds = []) =>
  client.get("/api/enrollments/photo-package/summary", {
    params: { studentIds: (studentIds || []).join(",") },
  });

export const submitBloodborneCert = (enrollmentId, file) => {
  const formData = new FormData();
  formData.append("file", file);

  return client.post(`/api/enrollments/${enrollmentId}/bloodborne-cert`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const submitQuizAttempt = (enrollmentId, sectionId, payload) =>
  client.post(`/api/enrollments/${enrollmentId}/sections/${sectionId}/quiz-attempt`, payload);

export const submitPostCourseAssignment = (enrollmentId, sectionId, { text, files }) => {
  const formData = new FormData();
  formData.append("text", text || "");
  (files || []).forEach((f) => formData.append("files", f));

  return client.post(`/api/enrollments/${enrollmentId}/post-course/${sectionId}/submit`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

/**
 * ✅ Review post-course assignment
 * Backend expects: { action: "approve" | "request_changes", feedback }
 *
 * Supports BOTH calling styles:
 *   1) reviewPostCourseAssignment(enrollmentId, sectionId, { action, feedback })
 *   2) reviewPostCourseAssignment({ enrollmentId, sectionId, action, feedback })
 */
export const reviewPostCourseAssignment = (arg1, arg2, arg3) => {
  // Object-style call
  if (arg1 && typeof arg1 === "object") {
    const { enrollmentId, sectionId, action, feedback, files } = arg1 || {};
    if (Array.isArray(files) && files.length) {
      const formData = new FormData();
      formData.append("action", action || "");
      formData.append("feedback", feedback || "");
      files.forEach((f) => formData.append("files", f));
      return client.post(`/api/enrollments/${enrollmentId}/post-course/${sectionId}/review`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }
    return client.post(`/api/enrollments/${enrollmentId}/post-course/${sectionId}/review`, {
      action,
      feedback: feedback || "",
    });
  }

  // Positional call
  const enrollmentId = arg1;
  const sectionId = arg2;
  const payload = arg3 || {};
  const { action, feedback, files } = payload;

  if (Array.isArray(files) && files.length) {
    const formData = new FormData();
    formData.append("action", action || "");
    formData.append("feedback", feedback || "");
    files.forEach((f) => formData.append("files", f));
    return client.post(`/api/enrollments/${enrollmentId}/post-course/${sectionId}/review`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  return client.post(`/api/enrollments/${enrollmentId}/post-course/${sectionId}/review`, {
    action,
    feedback: feedback || "",
  });
};

export const fetchPostCoursePendingSummary = () =>
  client.get("/api/enrollments/postcourse/pending-summary");

// ✅ NEW: student self-pass for "no assignment" sections
export const markPostCourseSectionPassed = (enrollmentId, sectionId) =>
  client.post(`/api/enrollments/${enrollmentId}/post-course/${sectionId}/mark-passed`);

export const submitPostCourseQuizAttempt = (enrollmentId, sectionId, payload) =>
  client.post(`/api/enrollments/${enrollmentId}/post-course/${sectionId}/quiz-attempt`, payload);
